/**
 * Admin API utilities
 * Handles all admin-related API calls using authFetch
 */
import { authFetch, setImpersonationToken, clearImpersonationToken, isImpersonating } from './auth';

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Impersonated user info key
const IMPERSONATED_USER_KEY = 'rc_impersonated_user';

/**
 * Get impersonated user info
 */
export function getImpersonatedUser() {
  const data = localStorage.getItem(IMPERSONATED_USER_KEY);
  return data ? JSON.parse(data) : null;
}

// Re-export isImpersonating from auth.js
export { isImpersonating };

/**
 * Clear impersonation
 */
export function clearImpersonation() {
  clearImpersonationToken();
  localStorage.removeItem(IMPERSONATED_USER_KEY);
}

/**
 * Admin API
 */
export const adminAPI = {
  /**
   * List all users
   */
  async listUsers(page = 1, pageSize = 20, search = '') {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (search) params.append('search', search);
    
    const response = await authFetch(`${API_BASE}/api/admin/users?${params}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch users');
    }
    return response.json();
  },

  /**
   * Get a specific user
   */
  async getUser(userId) {
    const response = await authFetch(`${API_BASE}/api/admin/users/${userId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch user');
    }
    return response.json();
  },

  /**
   * Get a user's orders
   */
  async getUserOrders(userId) {
    const response = await authFetch(`${API_BASE}/api/admin/users/${userId}/orders`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch user orders');
    }
    return response.json();
  },

  /**
   * List all orders (admin only)
   */
  async listAllOrders(page = 1, pageSize = 20, statusFilter = null) {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (statusFilter) params.append('status', statusFilter);
    
    const response = await authFetch(`${API_BASE}/api/admin/orders?${params}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch orders');
    }
    return response.json();
  },

  /**
   * Get any order by ID
   */
  async getOrder(orderId) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch order');
    }
    return response.json();
  },

  /**
   * Update order status (admin only)
   */
  async updateOrderStatus(orderId, status) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/status`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ status }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to update order status');
    }
    return response.json();
  },

  /**
   * Update user role (admin/user)
   */
  async updateUserRole(userId, role) {
    const response = await authFetch(`${API_BASE}/api/admin/users/${userId}/role`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to update user role');
    }
    return response.json();
  },

  /**
   * Get presigned download URL for any file
   */
  async getFileDownloadUrl(fileId) {
    const response = await authFetch(`${API_BASE}/api/admin/files/presigned-download/${fileId}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to get download URL');
    }
    return response.json();
  },

  /**
   * Start impersonation - gets a backend-signed impersonation token
   */
  async startImpersonation(userId) {
    const response = await authFetch(`${API_BASE}/api/admin/impersonate/start`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ user_id: userId }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to start impersonation');
    }
    const data = await response.json();
    
    // Store impersonation token (backend-signed, separate from Supabase)
    setImpersonationToken(data.access_token);
    localStorage.setItem(IMPERSONATED_USER_KEY, JSON.stringify({
      id: data.impersonated_user_id,
      email: data.impersonated_email,
    }));
    
    return data;
  },

  /**
   * End impersonation
   */
  async endImpersonation() {
    try {
      if (isImpersonating()) {
        await authFetch(`${API_BASE}/api/admin/impersonate/end`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
        });
      }
    } catch (e) {
      // Ignore errors, just clear local state
    }
    clearImpersonation();
  },

  /**
   * Get audit logs
   */
  async getAuditLogs(page = 1, pageSize = 50, action = null) {
    const params = new URLSearchParams({
      page: page.toString(),
      page_size: pageSize.toString(),
    });
    if (action) params.append('action', action);
    
    const response = await authFetch(`${API_BASE}/api/admin/audit-logs?${params}`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch audit logs');
    }
    return response.json();
  },

  /**
   * Get presigned upload URL for large deliverables (up to 300MB)
   */
  async getDeliverableUploadUrl(orderId, filename, contentType, sizeBytes) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/deliverables/presigned-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        filename,
        content_type: contentType,
        size_bytes: sizeBytes,
      }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to get upload URL');
    }
    return response.json();
  },

  /**
   * Confirm deliverable upload to S3
   */
  async confirmDeliverableUpload(orderId, fileId) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/deliverables/confirm-upload/${fileId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to confirm upload');
    }
    return response.json();
  },

  /**
   * Upload a deliverable file (complete flow: get presigned URL, upload to S3, confirm)
   */
  async uploadDeliverable(orderId, file, onProgress = null) {
    // Step 1: Get presigned URL
    const { file_id, upload_url } = await this.getDeliverableUploadUrl(
      orderId,
      file.name,
      file.type || 'application/octet-stream',
      file.size
    );
    
    // Step 2: Upload to S3
    const xhr = new XMLHttpRequest();
    
    await new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
      
      xhr.open('PUT', upload_url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
    
    // Step 3: Confirm upload
    await this.confirmDeliverableUpload(orderId, file_id);
    
    return { file_id };
  },

  // ═══════════════════════════════════════════════════════════════════════════
  // REVISION WORKFLOW APIs
  // ═══════════════════════════════════════════════════════════════════════════

  /**
   * Update order plan (max_revisions)
   */
  async updateOrderPlan(orderId, code, maxRevisions) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/plan`, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code, max_revisions: maxRevisions }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to update order plan');
    }
    return response.json();
  },

  /**
   * Get presigned upload URL for revision file
   */
  async getRevisionUploadUrl(orderId, kind, filename, contentType, sizeBytes) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/revisions/presigned-upload`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        kind,
        filename,
        content_type: contentType,
        size_bytes: sizeBytes,
      }),
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to get revision upload URL');
    }
    return response.json();
  },

  /**
   * Confirm revision upload to S3
   */
  async confirmRevisionUpload(orderId, revisionId) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/revisions/confirm-upload/${revisionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
    });
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to confirm revision upload');
    }
    return response.json();
  },

  /**
   * Upload a revision file (complete flow: get presigned URL, upload to S3, confirm)
   */
  async uploadRevision(orderId, file, kind, onProgress = null) {
    // Step 1: Get presigned URL
    const { revision_id, upload_url } = await this.getRevisionUploadUrl(
      orderId,
      kind,
      file.name,
      file.type || 'application/octet-stream',
      file.size
    );
    
    // Step 2: Upload to S3
    const xhr = new XMLHttpRequest();
    
    await new Promise((resolve, reject) => {
      xhr.upload.addEventListener('progress', (event) => {
        if (event.lengthComputable && onProgress) {
          const percent = Math.round((event.loaded / event.total) * 100);
          onProgress(percent);
        }
      });
      
      xhr.addEventListener('load', () => {
        if (xhr.status >= 200 && xhr.status < 300) {
          resolve();
        } else {
          reject(new Error(`Upload failed with status ${xhr.status}`));
        }
      });
      
      xhr.addEventListener('error', () => reject(new Error('Upload failed')));
      xhr.addEventListener('abort', () => reject(new Error('Upload aborted')));
      
      xhr.open('PUT', upload_url);
      xhr.setRequestHeader('Content-Type', file.type || 'application/octet-stream');
      xhr.send(file);
    });
    
    // Step 3: Confirm upload
    await this.confirmRevisionUpload(orderId, revision_id);
    
    return { revision_id };
  },

  /**
   * List all revisions for an order
   */
  async listRevisions(orderId) {
    const response = await authFetch(`${API_BASE}/api/admin/orders/${orderId}/revisions`);
    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || 'Failed to fetch revisions');
    }
    return response.json();
  },
};

export default adminAPI;
